import { useState, useRef, useEffect } from 'react'
import {
  chatKnowledge,
  defaultResponse,
  defaultArticle,
} from '../data/chatKnowledge'
import type { ArticleLink } from '../data/chatKnowledge'

export interface Message {
  id: string
  text: string
  sender: 'user' | 'bot'
  timestamp: Date
  suggestions?: string[]
  articleLink?: ArticleLink
}

export function useChatBot() {
  const [isOpen, setIsOpen] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'welcome',
      text: "Hi! I'm your AI productivity mentor, trained on insights from The Productivity Blueprint. What productivity challenge can I help you with today?",
      sender: 'bot',
      timestamp: new Date(),
      suggestions: [
        'How do I stay focused?',
        "What's a rolling time-block?",
        'Help me build a morning routine',
        'I struggle with attention',
      ],
    },
  ])

  const messagesEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages, isTyping, isOpen])

  const findResponse = (input: string) => {
    const lowerInput = input.toLowerCase()

    const match = chatKnowledge.find((entry) =>
      entry.keywords.some((keyword) => lowerInput.includes(keyword)),
    )

    if (match) {
      return {
        text: match.response,
        suggestions: match.followUp,
        articleLink: match.relatedArticle,
      }
    }

    return {
      text: defaultResponse,
      suggestions: ['Tell me about time blocking', 'How to focus better'],
      articleLink: defaultArticle,
    }
  }

  const sendMessage = async (text: string) => {
    if (!text.trim()) return

    const userMsg: Message = {
      id: Date.now().toString(),
      text,
      sender: 'user',
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMsg])
    setIsTyping(true)

    const delay = Math.random() * 1000 + 800

    setTimeout(() => {
      const response = findResponse(text)

      const botMsg: Message = {
        id: (Date.now() + 1).toString(),
        text: response.text,
        sender: 'bot',
        timestamp: new Date(),
        suggestions: response.suggestions,
        articleLink: response.articleLink,
      }

      setMessages((prev) => [...prev, botMsg])
      setIsTyping(false)
    }, delay)
  }

  const toggleChat = () => setIsOpen(!isOpen)

  return {
    isOpen,
    toggleChat,
    messages,
    sendMessage,
    isTyping,
    messagesEndRef,
  }
}
